import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  FormControlLabel,
  Switch,
  CircularProgress,
} from '@mui/material';
import { useCreatePeriodo, useUpdatePeriodo } from '../../hooks/usePeriodos';
import type { Periodo } from '../../types/periodo';

const schema = z.object({
  periodo: z
    .string({ required_error: 'Período es requerido' })
    .trim()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Período debe tener formato YYYY-MM (ej: 2026-03)'),
  fecha_vencimiento: z
    .string({ required_error: 'Fecha de vencimiento es requerida' })
    .min(1, 'Fecha de vencimiento es requerida')
    .refine(
      (v) => /^\d{4}-\d{2}-\d{2}$/.test(v),
      'Fecha debe tener formato YYYY-MM-DD',
    ),
  version: z
    .number({ invalid_type_error: 'Versión debe ser numérica' })
    .int('Versión debe ser un entero')
    .min(1, 'Versión debe ser mayor o igual a 1')
    .max(99, 'Versión no puede superar 99'),
  estado: z.enum(['activo', 'inactivo']),
  descripcion: z
    .string()
    .trim()
    .max(200, 'Descripción no puede superar 200 caracteres')
    .optional()
    .or(z.literal('')),
  excepcional: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const defaultValues: FormValues = {
  periodo: new Date().toISOString().slice(0, 7), // YYYY-MM actual
  fecha_vencimiento: '',
  version: 1,
  estado: 'activo',
  descripcion: '',
  excepcional: false,
};

interface Props {
  open: boolean;
  periodo: Periodo | null;
  onClose: () => void;
}

export default function PeriodoDialog({ open, periodo, onClose }: Props) {
  const isEdit = !!periodo;
  const create = useCreatePeriodo();
  const update = useUpdatePeriodo();

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    if (open) {
      reset(
        periodo
          ? {
              periodo: periodo.periodo,
              fecha_vencimiento: periodo.fecha_vencimiento,
              version: periodo.version,
              estado: periodo.estado,
              descripcion: periodo.descripcion ?? '',
              excepcional: periodo.excepcional,
            }
          : defaultValues
      );
    }
  }, [open, periodo, reset]);

  const excepcional = watch('excepcional');

  const onSubmit = async (values: FormValues) => {
    const payload = {
      periodo: values.periodo,
      fecha_vencimiento: values.fecha_vencimiento,
      version: values.version,
      estado: values.estado,
      descripcion: values.descripcion || null,
      excepcional: values.excepcional,
    };
    try {
      if (isEdit && periodo) {
        await update.mutateAsync({ id: periodo.id, payload });
      } else {
        await create.mutateAsync(payload);
      }
      onClose();
    } catch {
      // se muestra abajo
    }
  };

  const apiError = (isEdit ? update.error : create.error) as Error | null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle>{isEdit ? 'Editar periodo' : 'Nuevo periodo'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {apiError && (
              <Alert severity="error">
                {apiError.message.includes('duplicate') || apiError.message.includes('unique')
                  ? 'Ya existe un periodo con esa combinacion (periodo + version + tipo)'
                  : apiError.message}
              </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                {...register('periodo')}
                required
                fullWidth
                label="Periodo"
                placeholder="2026-03"
                disabled={isEdit}
                error={!!errors.periodo}
                helperText={
                  errors.periodo?.message ?? 'Formato YYYY-MM (ano-mes)'
                }
              />
              <Controller
                name="version"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? 1 : parseInt(e.target.value, 10))
                    }
                    type="number"
                    required
                    fullWidth
                    label="Version"
                    error={!!errors.version}
                    helperText={
                      errors.version?.message ?? 'Util si hay reliquidaciones'
                    }
                    inputProps={{ min: 1, max: 99 }}
                  />
                )}
              />
            </Stack>

            <TextField
              {...register('fecha_vencimiento')}
              type="date"
              required
              fullWidth
              label="Fecha de vencimiento"
              InputLabelProps={{ shrink: true }}
              error={!!errors.fecha_vencimiento}
              helperText={errors.fecha_vencimiento?.message ?? 'Limite para pagar sin mora'}
            />

            <Controller
              name="excepcional"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={
                    field.value
                      ? 'Periodo excepcional (aguinaldo, bono, reliquidacion)'
                      : 'Periodo regular (liquidacion mensual)'
                  }
                />
              )}
            />

            <TextField
              {...register('descripcion')}
              fullWidth
              label={excepcional ? 'Descripcion del periodo excepcional' : 'Descripcion (opcional)'}
              placeholder={excepcional ? 'Aguinaldo, Bono fin de ano...' : 'Liquidacion mensual'}
              error={!!errors.descripcion}
              helperText={errors.descripcion?.message}
            />

            <Controller
              name="estado"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value === 'activo'}
                      onChange={(e) => field.onChange(e.target.checked ? 'activo' : 'inactivo')}
                    />
                  }
                  label={field.value === 'activo' ? 'Activo' : 'Inactivo'}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {isSubmitting ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear periodo'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
